import React from 'react';
import { Typography, Paper, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

dayjs.locale('it');

const FinanceBalance = ({ data }) => {
  // Group data by month-year
  const monthlyData = {};

  data.forEach((item) => {
    const monthKey = dayjs(item.createdAt).format('MMMM YY');

    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = {
        month: monthKey,
        income: 0,
        expenses: 0,
        net: 0,
      };
    }

    if (item.amount > 0) {
      // Income (positive amounts)
      monthlyData[monthKey].income += item.amount;
    } else {
      // Expenses (negative amounts)
      monthlyData[monthKey].expenses += Math.abs(item.amount);
    }

    monthlyData[monthKey].net = monthlyData[monthKey].income - monthlyData[monthKey].expenses;
  });

  // Convert to array and sort by month (most recent first)
  const sortedData = Object.values(monthlyData).sort((a, b) => {
    const dateA = dayjs(a.month, 'MMMM YY');
    const dateB = dayjs(b.month, 'MMMM YY');
    return dateB.diff(dateA); // Most recent first
  });

  const formatAmount = (amount) => {
    return `${amount > 0 ? '+' : ''}${amount.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}€`;
  };

  const getNetColor = (net) => {
    if (net > 0) return 'success';
    if (net < 0) return 'error';
    return 'default';
  };

  const columns = [
    {
      field: 'month',
      headerName: 'Mese',
      flex: 1,
      // width: 120,
      sortable: true,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight='medium'>
          {params.value}
        </Typography>
      ),
      sortComparator: (v1, v2) => {
        // Custom sort comparator for Italian month names
        const date1 = dayjs(v1, 'MMMM YY', 'it');
        const date2 = dayjs(v2, 'MMMM YY', 'it');
        return date1.diff(date2);
      },
    },
    {
      field: 'income',
      headerName: 'Ingressi',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant='body2' color='success.main'>
          {formatAmount(params.value)}
        </Typography>
      ),
    },
    {
      field: 'expenses',
      headerName: 'Uscite',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant='body2' color='error.main'>
          {formatAmount(-params.value)}
        </Typography>
      ),
    },
    {
      field: 'net',
      headerName: 'Bilancio',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => <Chip label={formatAmount(params.value)} color={getNetColor(params.value)} size='small' variant={params.value === 0 ? 'outlined' : 'filled'} />,
    },
  ];

  if (sortedData.length === 0) {
    return (
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant='h6' gutterBottom>
          Bilancio Mensile
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Nessun dato disponibile
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 2, maxWidth: 'md' }}>
      <Typography variant='h6' gutterBottom>
        Bilancio
      </Typography>

      <DataGrid
        rows={sortedData}
        columns={columns}
        hideFooter
        disableRowSelectionOnClick
        // disableColumnMenu
        // disableColumnFilter
        // disableColumnSelector
        // disableDensitySelector
        getRowId={(row) => row.month}
        initialState={{
          sorting: {
            sortModel: [{ field: 'month', sort: 'desc' }], // Most recent first by default
          },
        }}
        sx={{
          backgroundColor: 'transparent',
          //   width: 'fit-content',
          //   minWidth: '570px', // Sum of column widths: 120 + 150 + 150 + 150
          '& .MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
            backgroundColor: '#fff',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#fff',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#fff',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            // justifyContent: 'center',
            alignItems: 'center',
          },
        }}
      />
    </Paper>
  );
};

export default FinanceBalance;
